<script setup lang="ts">
/**
 * The content/credential type label (CLE, CPE, Skills, Simulation, …) — the one
 * rendering every surface uses (BPE-755).
 *
 * Colour and icon both come from the type code, through `tagVariant()` and
 * `tagIcon()` in `utils/credentialTypes.ts`, so no surface decides either. The
 * label defaults to the kind's display label; pass a slot when a surface shows
 * something else ("1.5 CLE", "Simulation - Beta", an admin-created credential
 * name). See UX-Specifications.md, "Type tags".
 */
import { computed } from 'vue';
import { kindLabel, tagIcon, tagVariant } from '@/utils/credentialTypes';

interface Props {
  /** Type code, any case: 'cle', 'CPE', 'Simulation', 'pathway', … */
  code: string;
  /** OneUX tag size. `xs` inside tables and table-like rows, `sm` elsewhere. */
  size?: 'xs' | 'sm' | 'md';
}

const props = defineProps<Props>();

const classes = computed(() => ['bb-tag', props.size ? `-size-${props.size}` : '', tagVariant(props.code)]);
</script>

<template>
  <span :class="classes">
    <bb-icon :name="tagIcon(code)" decorative></bb-icon><slot>{{ kindLabel(code) }}</slot>
  </span>
</template>
